import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  ArrowLeftRight, Banknote, BookOpen, Briefcase, Building2, CalendarPlus, ClipboardList, Factory, FilePlus2, FileSignature, FileText, HandCoins, Headset,
  Inbox, Package, Plus, Receipt, ShoppingCart, Truck, UserPlus, type LucideIcon,
} from "lucide-react";
import { hrefParts } from "./apps";
import { useCreates } from "./creates";

/** Each thing to create, by its label, with an icon that says what it is. */
export const createIcon: Record<string, LucideIcon> = {
  "create.invoice": FilePlus2, "create.payment": HandCoins, "create.quote": FileText, "create.expense": Receipt, "create.lead": UserPlus,
  "create.organization": Building2, "create.request": Inbox, "create.appointment": CalendarPlus, "create.salesOrder": ClipboardList, "create.po": ShoppingCart,
  "create.ticket": Headset, "create.project": Briefcase, "create.employee": UserPlus, "create.product": Package, "create.delivery": Truck, "create.entry": BookOpen,
  "create.receiptVoucher": Banknote, "create.paymentVoucher": Banknote, "create.moneyTransfer": ArrowLeftRight, "create.productionOrder": Factory, "create.saleContract": FileSignature,
};

/** The few things the user starts most often, a tap away on the home page: pills on a wide screen, tiles on a phone. */
export function QuickActions() {
  const { t } = useTranslation();
  const creates = useCreates().slice(0, 4);
  if (creates.length === 0) return null;
  return (
    <div role="group" aria-label={t("dashboard.quick")} className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap">
      {creates.map((c, i) => {
        const Icon = createIcon[c.label] ?? Plus;
        const { to, search } = hrefParts(c.to);
        return (
          <Link key={c.to} to={to} search={search as never}
            className={clsx(
              "flex flex-col items-center gap-1.5 rounded-2xl bg-paper px-1 py-3 text-center text-[12.5px] font-medium transition-colors",
              "sm:flex-row sm:gap-2 sm:rounded-full sm:px-4 sm:py-2 sm:text-[14px]",
              i === 0 ? "sm:bg-ink sm:text-white sm:hover:bg-ink/90" : "sm:border sm:border-line sm:bg-surface sm:hover:bg-paper",
              i >= 4 && "max-sm:hidden",
            )}>
            <Icon className={clsx("size-5 text-ledger sm:size-[17px]", i === 0 && "sm:text-white")} aria-hidden />
            {t(c.label)}
          </Link>
        );
      })}
    </div>
  );
}
